import React from 'react';
import { Text } from '@clockout-dev/design-elements';
import AccountBalanceCard from './AccountBalanceCard';
import FundingRecommendationCard from './FundingRecommendationCard';
import KpiAdvanceRow from './KpiAdvanceRow';
import KpiFeeRow from './KpiFeeRow';
import CashflowChartCard from './CashflowChartCard';
import AgingReceivablesCard from './AgingReceivablesCard';
import CardSkeleton from '../../common/cardSkeleton/CardSkeleton';
import DashboardPeriodControls from '../../dashboard/elements/DashboardPeriodControls';
import { GL_METRICS_LABEL, GL_TYPE } from '../treasury.constants';
import styles from './styles/glAccountSection.module.scss';

/**
 * Pure presentational shell that arranges every GL Account card. The
 * orchestrator passes:
 *
 *   - `glType` — active tab (drives the KPI row variant)
 *   - `account.{ data, loaded }`
 *   - `funding.{ data, loaded }`
 *   - `kpis.{ data, loaded }`
 *   - `cashflow.{ data, loaded, grouping, onGroupingChange }`
 *   - `aging.{ data, loaded }`
 *   - `period.{ timeFrame, dateRange }` + `onTimeFrameChange` + `onDateRangeChange`
 *   - `onViewOverdueUsers`
 *
 * No state, no fetch, no `this.data` access — every value lands here as
 * props. While a slot is loading we render the shared `CardSkeleton`
 * placeholder so the layout stays stable.
 *
 * The KPI row is the only slot that branches on `glType`: Advance and
 * Fee tabs surface a different set of metrics (different labels,
 * different delta pills) so each has its own dedicated row component.
 */
function GLAccountSection({
    glType,
    account,
    funding,
    kpis,
    cashflow,
    aging,
    period,
    onTimeFrameChange,
    onDateRangeChange,
    onViewOverdueUsers,
}) {
    return (
        <div className={styles.section}>
            <div className={styles.topRow}>
                {account?.loaded
                    ? <AccountBalanceCard data={account.data} />
                    : <CardSkeleton minHeight={220} />}
                {funding?.loaded
                    ? <FundingRecommendationCard data={funding.data} />
                    : <CardSkeleton minHeight={220} />}
            </div>

            <div className={styles.metricsHeader}>
                <Text as="p" size="l" className={styles.sectionLabel}>{GL_METRICS_LABEL[glType]}</Text>
                <DashboardPeriodControls
                    timeFrame={period?.timeFrame}
                    dateRange={period?.dateRange}
                    onTimeFrameChange={onTimeFrameChange}
                    onDateRangeChange={onDateRangeChange}
                />
            </div>

            <div className={styles.kpis}>
                {kpis?.loaded && glType === GL_TYPE.ADVANCE
                    ? <KpiAdvanceRow data={kpis.data} onViewOverdueUsers={onViewOverdueUsers} />
                    : kpis?.loaded && glType === GL_TYPE.FEE ? <KpiFeeRow data={kpis.data} />
                        : <CardSkeleton minHeight={150} rows={2} />}
            </div>

            <div className={styles.chartsRow}>
                {cashflow?.loaded
                    ? (
                        <CashflowChartCard
                            data={cashflow.data}
                            grouping={cashflow.grouping}
                            onGroupingChange={cashflow.onGroupingChange}
                        />
                    )
                    : <CardSkeleton minHeight={420} rows={6} />}
                {aging?.loaded
                    ? <AgingReceivablesCard data={aging.data} />
                    : <CardSkeleton minHeight={420} rows={6} />}
            </div>
        </div>
    );
}

export default GLAccountSection;
